import type { Component } from 'vue';

export type QuestionAnswer = {
  id: number;
  text: string;
};

export type QuestionData = {
  title: string;
  type: 'radio' | 'checkbox';
  answers: QuestionAnswer[];
};

export type FunnelStep = {
  id: string;
  type: 'question' | 'info' | 'auto';
  component: () => Promise<{ default: Component }>;
  meta?: {
    needBackground?: boolean;
    quizIndex?: number;
  };
  questionData?: QuestionData;
};

export const FUNNEL_STEPS: FunnelStep[] = [
  {
    id: 'index',
    type: 'info',
    component: () => import('@/modules/spam-blocker/pages/index/PageIndex.vue'),
  },

  // Q1
  {
    id: 'q1',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 0 },
    questionData: {
      title: 'Do you get spam calls?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes' },
        { id: 2, text: 'No' },
      ],
    },
  },

  // Q2
  {
    id: 'q2',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 1 },
    questionData: {
      title: 'Are you tired of spam calls?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes 😤' },
        { id: 2, text: 'No 😌' },
      ],
    },
  },

  // Info: email-spam
  {
    id: 'email-spam',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/email-spam/PageEmailSpam.vue'),
    meta: { needBackground: true },
  },

  // Q3
  {
    id: 'q3',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 2 },
    questionData: {
      title: 'Have you tried blocking spam callers before?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes — but no success, still calling' },
        { id: 2, text: "No — Don't know how to block them" },
      ],
    },
  },

  // Q4
  {
    id: 'q4',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 3 },
    questionData: {
      title:
        'Have you gotten calls that looked official, but turned out to be scams?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes — hard to tell real from fake' },
        { id: 2, text: "Not yet — but I'm worried about it" },
      ],
    },
  },

  // Info: protect-personal-data
  {
    id: 'protect-personal-data',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/protect-personal-data/PageProtectPersonalData.vue'),
    meta: { needBackground: true },
  },

  // Q5
  {
    id: 'q5',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 4 },
    questionData: {
      title: 'How many unknown or spam calls do you receive daily?',
      type: 'radio',
      answers: [
        { id: 1, text: '10+ calls 😤' },
        { id: 2, text: '5–10 calls 😠' },
        { id: 3, text: '1–4 calls 😒' },
      ],
    },
  },

  // Info: same-password
  {
    id: 'same-password',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/same-password/PageSamePassword.vue'),
    meta: { needBackground: true },
  },

  // Q6
  {
    id: 'q6',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 5 },
    questionData: {
      title:
        'Have you ever gotten a call where the unknown person knew your name?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes — no idea how they got it 😨' },
        { id: 2, text: 'No — but that scares me' },
      ],
    },
  },

  // Info: protect-info
  {
    id: 'protect-info',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/protect-info/PageProtectInfo.vue'),
    meta: { needBackground: true },
  },

  // Q7
  {
    id: 'q7',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 6 },
    questionData: {
      title: 'Do you feel spam calls are getting more out of control?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes 😤' },
        { id: 2, text: 'No 😌' },
      ],
    },
  },

  // Q8
  {
    id: 'q8',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 7 },
    questionData: {
      title: 'Do you sometimes avoid answering calls from unknown numbers?',
      type: 'radio',
      answers: [
        { id: 1, text: "Yes — I'm always suspicious" },
        { id: 2, text: 'No — but maybe I should be' },
      ],
    },
  },

  // Info: online-threats
  {
    id: 'online-threats',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/online-threats/PageOnlineThreats.vue'),
    meta: { needBackground: true },
  },

  // Q9
  {
    id: 'q9',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 8 },
    questionData: {
      title: 'Have you ever regretted sharing personal info online?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes 😞' },
        { id: 2, text: 'No 😐' },
      ],
    },
  },

  // Q10 (checkbox)
  {
    id: 'q10',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 9 },
    questionData: {
      title: 'Which of this personal information have you ever shared online?',
      type: 'checkbox',
      answers: [
        { id: 1, text: 'Phone number or email' },
        { id: 2, text: 'Home address' },
        { id: 3, text: 'Bank or card information' },
        { id: 4, text: 'Passwords' },
      ],
    },
  },

  // Q11
  {
    id: 'q11',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 10 },
    questionData: {
      title:
        'Are you worried that scammers may already have your phone number or email?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes — I don’t know how they got it' },
        { id: 2, text: 'A little' },
        { id: 3, text: 'I’ve never thought about it' },
      ],
    },
  },

  // Info: reviews
  {
    id: 'reviews',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/reviews/PageReviews.vue'),
    meta: { needBackground: true },
  },

  // Q12
  {
    id: 'q12',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 11 },
    questionData: {
      title: 'When was the last time you felt secure online?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Never 😞' },
        { id: 2, text: 'A very long time ago ☹️' },
        { id: 3, text: 'Recently 😐' },
        { id: 4, text: 'Still feel secure 🙂' },
      ],
    },
  },

  // Q13
  {
    id: 'q13',
    type: 'question',
    component: () => import('@/modules/spam-blocker/pages/quiz/PageQuiz.vue'),
    meta: { quizIndex: 12 },
    questionData: {
      title: 'Are you ready to take back control of your personal data?',
      type: 'radio',
      answers: [
        { id: 1, text: 'Yes, I’m ready' },
        { id: 2, text: 'Not sure yet' },
      ],
    },
  },

  // Info: check-data
  {
    id: 'check-data',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/check-data/PageCheckData.vue'),
    meta: { needBackground: true },
  },

  // Loader (auto-advances)
  {
    id: 'loader',
    type: 'auto',
    component: () =>
      import('@/modules/spam-blocker/pages/loader/PageLoader.vue'),
  },

  // Result
  {
    id: 'result',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/result/PageResult.vue'),
    meta: { needBackground: true },
  },

  // Protect plan
  {
    id: 'protect-plan',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/protect-plan/PageProtectPlan.vue'),
    meta: { needBackground: true },
  },

  // Preparing (auto-advances)
  {
    id: 'preparing',
    type: 'auto',
    component: () =>
      import('@/modules/spam-blocker/pages/preparing/PagePreparing.vue'),
    meta: { needBackground: true },
  },

  // Auth
  {
    id: 'auth',
    type: 'info',
    component: () => import('@/modules/spam-blocker/pages/auth/PageAuth.vue'),
  },

  // Payment (terminal step)
  {
    id: 'payment',
    type: 'info',
    component: () =>
      import('@/modules/spam-blocker/pages/payment/PagePayment.vue'),
  },
];

export const STEP_MAP = new Map(FUNNEL_STEPS.map((step) => [step.id, step]));

export const QUESTION_STEPS = FUNNEL_STEPS.filter(
  (step) => step.type === 'question',
);

export const BACKGROUND_STEP_IDS = new Set(
  FUNNEL_STEPS.filter((step) => step.meta?.needBackground).map(
    (step) => step.id,
  ),
);
